import { Home, Layers, Key, Search, PanelLeftClose, PanelLeft } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/ui';

const navItems = [
  { to: '/', icon: Home, label: 'Home' },
  { to: '/applications', icon: Layers, label: 'Applications' },
  { to: '/keys', icon: Key, label: 'API Keys' },
];

export function Sidebar() {
  const {
    sidebarCollapsed,
    sidebarHovered,
    setSidebarHovered,
    toggleSidebar,
    setQuickSearchOpen,
  } = useUIStore();

  const showExpanded = !sidebarCollapsed || sidebarHovered;

  return (
    <>
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 flex h-screen flex-col overflow-hidden border-r border-slate-200 bg-white transition-[width] duration-300 ease-in-out dark:border-slate-800 dark:bg-black',
          showExpanded ? 'w-64' : 'w-14'
        )}
        onMouseEnter={() => sidebarCollapsed && setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        <div className="flex h-14 items-center gap-3 border-b border-slate-200 px-4 dark:border-slate-800">
          <img src="/logo.svg" alt="Outpost9" className="h-5 w-auto flex-shrink-0" />
          <span
            className={cn(
              'whitespace-nowrap font-semibold text-slate-900 transition-opacity duration-300 dark:text-white',
              showExpanded ? 'opacity-100' : 'opacity-0'
            )}
          >
            Outpost9
          </span>
        </div>

        <nav className="flex-1 space-y-1 p-2">
          <button
            onClick={() => setQuickSearchOpen(true)}
            className={cn(
              'flex w-full items-center gap-3 rounded-button px-3 py-2 text-xs text-slate-600 transition-all duration-300 dark:text-slate-400',
              showExpanded
                ? 'mb-2 border border-slate-200 bg-surface-hover hover:bg-surface-hover-deep dark:border-slate-700 dark:bg-surface-hover-dark dark:hover:bg-surface-hover-deep-dark dark:hover:text-white'
                : 'mb-0 border border-transparent bg-transparent hover:bg-surface-hover dark:hover:bg-surface-hover-dark dark:hover:text-white'
            )}
          >
            <Search className="h-4 w-4 flex-shrink-0" />
            <span
              className={cn(
                'flex-1 whitespace-nowrap text-left transition-opacity duration-300',
                showExpanded ? 'opacity-100' : 'opacity-0'
              )}
            >
              Quick search
            </span>
            <span
              className={cn(
                'whitespace-nowrap text-xs text-slate-400 transition-opacity duration-300 dark:text-slate-500',
                showExpanded ? 'opacity-100' : 'opacity-0'
              )}
            >
              ⌘K
            </span>
          </button>

          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-button px-3 py-2 text-xs font-medium transition-colors',
                  isActive
                    ? 'bg-surface-selected text-slate-900 dark:bg-surface-selected-dark dark:text-white'
                    : 'text-slate-600 hover:bg-surface-hover dark:text-slate-400 dark:hover:bg-surface-hover-dark dark:hover:text-white'
                )
              }
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              <span
                className={cn(
                  'whitespace-nowrap transition-opacity duration-300',
                  showExpanded ? 'opacity-100' : 'opacity-0'
                )}
              >
                {item.label}
              </span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-200 p-2 dark:border-slate-800">
          <button
            onClick={toggleSidebar}
            className="flex w-full items-center gap-3 rounded-button px-3 py-2 text-xs text-slate-500 transition-colors hover:bg-surface-hover dark:text-slate-400 dark:hover:bg-surface-hover-dark dark:hover:text-white"
          >
            {sidebarCollapsed ? (
              <PanelLeft className="h-4 w-4 flex-shrink-0" />
            ) : (
              <PanelLeftClose className="h-4 w-4 flex-shrink-0" />
            )}
            <span
              className={cn(
                'whitespace-nowrap transition-opacity duration-300',
                showExpanded ? 'opacity-100' : 'opacity-0'
              )}
            >
              {sidebarCollapsed ? 'Expand' : 'Collapse'}
            </span>
          </button>
        </div>
      </aside>

      {sidebarCollapsed && sidebarHovered && (
        <div
          className="fixed inset-0 z-30 bg-black/20 backdrop-blur-sm"
          onClick={() => setSidebarHovered(false)}
        />
      )}
    </>
  );
}
